import { Href, useFocusEffect, useRouter } from 'expo-router';
import useBelieveAccount from 'features/believe/application/hooks/useBelieveAccount';
import useBelieveApplicationPending from 'features/believe/application/hooks/useBelieveApplicationPending';
import { IncentivePrerequisite } from 'features/incentive/prerequisite/incentivePrerequisite';
import { useTranslation } from 'react-i18next';
import useEvent from 'shared/hooks/useEvent/useEvent';
import useFlowReturnRoute, {
  FLOW_RETURN_ROUTE_PARAM,
} from 'shared/hooks/useFlowReturnRoute';

const useSweepstakesPrerequisite = (): IncentivePrerequisite => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('incentive', {
    keyPrefix: 'sweepstakes',
  });
  const { account, fetching, error, refresh } = useBelieveAccount();
  const {
    isPending: isApplicationPending,
    fetching: isApplicationFetching,
    error: applicationError,
    refresh: refreshApplication,
  } = useBelieveApplicationPending();
  const { registerReturnRoute, clearReturnRoute } =
    useFlowReturnRoute('believeApplication');

  const handleFocus = useEvent(() => {
    clearReturnRoute();
  });

  useFocusEffect(handleFocus);

  const start = (returnRoute: Href) => {
    registerReturnRoute(returnRoute);
    router.push({
      pathname: '/believe/apply',
      params: { [FLOW_RETURN_ROUTE_PARAM]: 'true' },
    });
  };

  const getIsMet = () => {
    if (account) {
      return true;
    }

    if (account === undefined || isApplicationPending === undefined) {
      return undefined;
    }

    return isApplicationPending;
  };

  const handleRefresh = () => {
    refresh();
    refreshApplication();
  };

  const getProduct = () => {
    if (!account) {
      return undefined;
    }

    const accountId = account.id;
    return {
      accountLastFour: account.accountNumberLast4 ?? undefined,
      label: translatedText('winner.believeAccount'),
      open: () => {
        router.push({ pathname: '/accounts/[id]', params: { id: accountId } });
      },
    };
  };

  return {
    isMet: getIsMet(),
    fetching: fetching || isApplicationFetching,
    error: error ?? applicationError,
    refresh: handleRefresh,
    start,
    sheetCopy: {
      title: translatedText('prerequisiteSheet.title'),
      description: translatedText('prerequisiteSheet.description'),
      disclaimer: translatedText('prerequisiteSheet.disclaimer'),
      startLabel: translatedText('prerequisiteSheet.applyForBelieve'),
      skipLabel: translatedText('prerequisiteSheet.skipSweepstakes'),
    },
    product: getProduct(),
  };
};

export default useSweepstakesPrerequisite;
